import { Component, Texture, Object3D, Property, Mesh, Material, MeshComponent } from '@wonderlandengine/api';
import { setFirstMaterialTexture, resizeToRatio } from './utils/utils.js';
import { property } from '@wonderlandengine/api/decorators.js';
import { EventsClient } from './events-client.js';
import Logger from './utils/logger.js';
import { PhongOpaque } from './materials.js';
import { UrlVideoSync } from './media/url-video-sync.js';
import { UserMessaging } from './utils/user-messaging.js';

const MODULE = 'NetworkListenVideo';

type VideoSourceKind = 'stream' | 'url';

/**
 * Downloads a video from URL and applies it as `diffuseTexture` or `flatTexture`
 * on given material.
 * 
 * Script based off original video-texture script provided by WonderlandEngine
 *
 * Video textures need to be updated regularly whenever
 * a new frame is available. This component handles the
 * detection of a new frame and updates the texture to
 * reflect the video's current frame.
 *
 * Materials from the following shaders are supported:
 *  - "Phong Opaque Textured"
 *  - "Flat Opaque Textured"
 *  - "Background"
 *  - "Physical Opaque Textured"
 *  - "Foliage"
 *
 * The video can be accessed through `this.video`:
 *
 * ```js
 *   let videoTexture = this.object.getComponent('video-texture');
 *   videoTexture.video.play();
 *   videoTexture.video.pause();
 * ```
 *
 * See [Video Example](/showcase/video).
 */
export class NetworkListenVideoTexture extends Component {
    static TypeName = 'networklisten-video-texture';

    /** Material to apply the video texture to */
    @property.material({ required: true })
    material!: Material;
    /** Whether to mute sound */
    @property.bool(true)
    muted!: boolean;
    /** Name of the texture property to set */
    @property.string('auto')
    textureProperty!: string;

    @property.bool(false)
    playerBased: boolean = false;

    @property.float(91.6)
    canvasMaxScaleWidth: number = 91.6;
    @property.float(52.2)
    canvasMaxScaleHeight: number = 52.2;

    //For now we're relying on the event client logic to handle remPlayer number
    remPlayer: number = -1;
    loaded = false;
    attemptingPlay = false;
    frameUpdateRequested = true;
    mesh: MeshComponent | null = null;

    video?: HTMLVideoElement;
    texture?: Texture;
    vidshareEventsClient!: EventsClient;
    
    private videoSizeChangeTimeout: number | undefined;
    private lastAppliedWidth: number = 0;
    private lastAppliedHeight: number = 0;
    private lastTextureRecreateTime: number = 0;
    private streamStartTime: number = 0;
    private isInitialTextureCreation: boolean = false;
    private firstResizeApplied: boolean = false;
    private initialResizeTimeout: number | undefined;
    private finalResizeTimeout: number | undefined;
    private currentSenderId: number | null = null;
    private currentSourceKind: VideoSourceKind | null = null;
    private currentTrackEndedHandler: (() => void) | null = null;
    private currentRemoveTrackHandler: ((event: MediaStreamTrackEvent) => void) | null = null;
    private masterSharerClaimHandler?: (sharerId: number) => void;
    private readonly urlSync = new UrlVideoSync();
    private ownsUrlAudio = false;
    private readonly onSyncUrlVideoStartHandler = this.onSyncUrlVideoStart.bind(this);
    private readonly onSyncUrlVideoPingHandler = this.onSyncUrlVideoPing.bind(this);

    /**
     * URL shares are fetched independently by every client, so the audio comes out of the
     * video element itself. Only one screen may claim it or the room hears echoes.
     */
    private static urlAudioOwner: NetworkListenVideoTexture | null = null;

    start() {
        this.vidshareEventsClient = EventsClient.instance;

        if (this.playerBased) {
            this.mesh = this.object.getComponent('mesh');
            if (!this.mesh) {
                throw new Error('Missing mesh component on the networklisten video texture object');
            } else {
                let tempMat = this.mesh.material?.clone();
                if (tempMat) {
                    this.material = tempMat;
                    this.mesh.material = tempMat;
                }
                this.mesh.active = false;
            }
        }

        this.listenForTracks();
    }

    listenForTracks() {
        // First, check for any existing active streams and play them immediately
        let playedExistingStream = false;
        for (const [senderId, stream] of this.vidshareEventsClient.activeVideoStreams.entries()) {
            // Check if this stream matches our filtering criteria
            let shouldPlayStream = false;
            if (this.playerBased) {
                shouldPlayStream = (senderId === this.remPlayer);
            } else {
                shouldPlayStream = (!this.vidshareEventsClient.masterSharerId || this.vidshareEventsClient.masterSharerId === senderId);
            }
            
            if (shouldPlayStream) {
                this.playVideo(stream, senderId);
                playedExistingStream = true;
            }
        }

        // Listen for new tracks going forward
        this.vidshareEventsClient.onShareEvent.add(this.onTrack.bind(this));
        this.vidshareEventsClient.onShareStopEvent.add(this.onShareStopped.bind(this));
        
        // For non-player textures: when master sharer is claimed (late joiner scenario),
        // try to play the stream from that sharer if available
        if (!this.playerBased) {
            this.masterSharerClaimHandler = (sharerId: number) => {
                if (sharerId > 0 && this.vidshareEventsClient.activeVideoStreams.has(sharerId)) {
                    const stream = this.vidshareEventsClient.activeVideoStreams.get(sharerId);
                    if (stream) {
                        this.playVideo(stream, sharerId);
                    }
                }
            };
            this.vidshareEventsClient.onClaimMasterShareEvent.add(this.masterSharerClaimHandler);

            // URL shares bypass RTCPeerConnection entirely, but they target the same screens.
            this.vidshareEventsClient.onSyncUrlVideoStartEvent.add(this.onSyncUrlVideoStartHandler);
            this.vidshareEventsClient.onSyncUrlVideoPingEvent.add(this.onSyncUrlVideoPingHandler);

            // Room screens live in externally loaded prefabs, so the start event has usually
            // already been delivered by the time this component runs. Catch up from the
            // retained room state instead of waiting for the next event.
            const activeUrlShare = this.vidshareEventsClient.activeUrlVideoShare;
            if (activeUrlShare && !playedExistingStream) {
                this.playUrlVideo(activeUrlShare.url, activeUrlShare.startAtMs, activeUrlShare.seekSeconds);
            }
        }
    }

    private onSyncUrlVideoStart(data: { url: string; startAtMs: number; requestedBy: number; seekSeconds: number }) {
        this.playUrlVideo(data.url, data.startAtMs, data.seekSeconds);
    }

    private onSyncUrlVideoPing(data: { url: string; startAtMs: number; requestedBy: number; seekSeconds: number }) {
        if (this.currentSourceKind !== 'url' || !this.video) {
            return;
        }

        this.urlSync.applyPing(this.video, data.url, data.startAtMs, data.seekSeconds);
    }

    /**
     * Plays a room-synchronized URL through the exact same texture/resize pipeline used by
     * peer-to-peer and mediasoup streams.
     */
    playUrlVideo(url: string, startAtMs: number, seekSeconds: number) {
        this.beginNewSource();

        if (!this.video) {
            Logger.warn(MODULE, 'Video element not initialized for shared URL playback');
            this.attemptingPlay = false;
            return;
        }

        if (!NetworkListenVideoTexture.urlAudioOwner || NetworkListenVideoTexture.urlAudioOwner === this) {
            NetworkListenVideoTexture.urlAudioOwner = this;
            this.ownsUrlAudio = true;
        }
        this.video.muted = this.muted || !this.ownsUrlAudio;

        const started = this.urlSync.begin(this.video, url, startAtMs, seekSeconds, {
            onPlay: (isLateJoinCatchup) => {
                this.frameUpdateRequested = true;
                if (isLateJoinCatchup) {
                    UserMessaging.instance?.notifyState('Synced to room stream');
                }
            },
            onError: (message) => {
                if (this.ownsUrlAudio) {
                    UserMessaging.instance?.notify(message, 8.0);
                }
                this.clearVideoSurface();
            },
        });

        if (!started) {
            this.attemptingPlay = false;
            this.clearVideoSurface();
            return;
        }

        this.currentSenderId = null;
        this.currentSourceKind = 'url';

        if (this.playerBased && this.mesh) {
            this.mesh.active = true;
        }

        const phongMaterial = this.material as PhongOpaque;
        phongMaterial.setDiffuseColor([0.75, 0.75, 0.75, 1]);
        this.frameUpdateRequested = true;
    }

    onShareStopped(senderId: number) {
        // A peer ending their share must not interrupt an active room-wide URL playback.
        if (this.currentSourceKind === 'url') {
            return;
        }

        if (this.playerBased) {
            // For player-based textures, only clear if:
            // 1. The stopping sender matches this texture's assigned remote player, OR
            // 2. We're currently displaying content AND the sender is who we're displaying

            const matchesAssignedPlayer = this.remPlayer > 0 && senderId === this.remPlayer;
            const matchesCurrentContent = this.currentSenderId !== null && senderId === this.currentSenderId;

            if (matchesAssignedPlayer || matchesCurrentContent) {
                Logger.debug(MODULE, `Player ${senderId} (assigned=${this.remPlayer}, current=${this.currentSenderId}) stopped, clearing texture`);
                this.clearVideoSurface();
            }
            return;
        }

        // Non-player-based views: only clear if:
        // 1. The sender matches the master sharer, OR
        // 2. We're currently displaying someone AND that someone just stopped broadcasting
        
        const matchesMasterSharer = this.vidshareEventsClient.masterSharerId > 0 && this.vidshareEventsClient.masterSharerId === senderId;
        const matchesCurrentContent = this.currentSenderId !== null && senderId === this.currentSenderId;

        if (matchesMasterSharer) {
            Logger.debug(MODULE, `Share stopped: sender=${senderId}, master=${this.vidshareEventsClient.masterSharerId}, current=${this.currentSenderId}. Clearing texture.`);
            this.clearVideoSurface();
        } else if (matchesCurrentContent) {
            const masterSharerId = this.vidshareEventsClient.masterSharerId;
            if (masterSharerId > 0 && masterSharerId !== senderId) {
                const masterStream = this.vidshareEventsClient.activeVideoStreams.get(masterSharerId);
                if (masterStream) {
                    Logger.debug(MODULE, `Current sender ${senderId} stopped; switching non-player texture back to master sharer ${masterSharerId}`);
                    this.playVideo(masterStream, masterSharerId);
                    return;
                }
            }

            Logger.debug(MODULE, `Share stopped for current sender ${senderId}; no alternate master stream available, clearing texture.`);
            this.clearVideoSurface();
        }
    }

    onTrack(senderId: number, e: RTCTrackEvent) {
        if(this.playerBased) {
            if (senderId == this.remPlayer) {
                this.playVideo(e.streams[0], senderId);
            }
        } else if (!this.vidshareEventsClient.masterSharerId || this.vidshareEventsClient.masterSharerId == senderId) {
            this.playVideo(e.streams[0], senderId);
        }
    }

    initVideo() {
        this.video = document.createElement('video');
        this.video.crossOrigin = 'anonymous';
        this.video.playsInline = true;
        this.video.muted = this.muted;
        this.video.addEventListener('canplaythrough', () => {
            this.loaded = true;
        });
        // URL sources can begin rendering well before 'canplaythrough' fires.
        this.video.addEventListener('playing', () => {
            this.loaded = true;
            this.frameUpdateRequested = true;
        });

        this.video.addEventListener('loadedmetadata', this.videoSizeChanged);
        this.video.onresize = this.videoSizeChanged;
    }

    videoSizeChanged = () => {
        // Debounce rapid resize events to avoid visual degradation
        // 100ms timeout allows orientation changes to process quickly
        if (this.videoSizeChangeTimeout !== undefined) {
            clearTimeout(this.videoSizeChangeTimeout);
        }
        
        this.videoSizeChangeTimeout = window.setTimeout(() => {
            this.applyVideoResize();
            this.videoSizeChangeTimeout = undefined;
        }, 100);
    }

    private applyVideoResize() {
        if(this.video && this.video.videoWidth > 0 && this.video.videoHeight > 0) {
            const now = Date.now();
            const timeSinceStreamStart = now - this.streamStartTime;

            // Force one resize at >=500ms even if dimensions haven't changed yet
            if (!this.firstResizeApplied && timeSinceStreamStart >= 500) {
                resizeToRatio(this.video.videoWidth, this.video.videoHeight, this.canvasMaxScaleWidth, this.canvasMaxScaleHeight, this.object);
                this.lastAppliedWidth = this.video.videoWidth;
                this.lastAppliedHeight = this.video.videoHeight;
                this.firstResizeApplied = true;
                return;
            }

            // Only apply resize if dimensions actually changed (filter spurious resize events)
            if (this.video.videoWidth !== this.lastAppliedWidth || this.video.videoHeight !== this.lastAppliedHeight) {
                Logger.debug(MODULE, `Video size changed from ${this.lastAppliedWidth}x${this.lastAppliedHeight} to ${this.video.videoWidth}x${this.video.videoHeight}`);
                
                // After stabilization period (1s), recreate texture on ANY dimension change
                // This handles Chrome's automatic quality adaptation that changes the actual stream resolution
                if (this.texture && timeSinceStreamStart > 1000) {
                    Logger.debug(MODULE, 'Video dimensions changed after stabilization, recreating texture');
                    this.texture.destroy();
                    this.texture = this.engine.textures.create(this.video);
                    const mat = this.material;
                    setFirstMaterialTexture(mat, this.texture, this.textureProperty);
                }
                
                this.lastAppliedWidth = this.video.videoWidth;
                this.lastAppliedHeight = this.video.videoHeight;
                
                // Defer resizing until at least 500ms to avoid early flashes
                if (timeSinceStreamStart >= 500) {
                    resizeToRatio(this.video.videoWidth, this.video.videoHeight, this.canvasMaxScaleWidth, this.canvasMaxScaleHeight, this.object);
                    this.firstResizeApplied = true;
                }
            }
        }
    }

    /**
     * Tears down whatever is currently on screen and prepares a fresh video element plus the
     * deferred resize schedule. Shared by the stream and URL source paths.
     */
    private beginNewSource() {
        this.detachCurrentStreamListeners();
        this.urlSync.clear();
        this.attemptingPlay = true;
        this.video?.pause();
        this.loaded = false;
        this.texture?.destroy();
        delete this.texture;
        this.lastAppliedWidth = 0;
        this.lastAppliedHeight = 0;
        this.streamStartTime = Date.now();
        this.lastTextureRecreateTime = 0;
        this.isInitialTextureCreation = true;
        this.firstResizeApplied = false;
        if (this.initialResizeTimeout !== undefined) {
            clearTimeout(this.initialResizeTimeout);
        }
        if (this.finalResizeTimeout !== undefined) {
            clearTimeout(this.finalResizeTimeout);
        }
        // Schedule a guaranteed resize after 800ms using latest dimensions to lock final aspect ratio
        this.initialResizeTimeout = window.setTimeout(() => {
            this.applyVideoResize();
        }, 800);
        // Schedule a final lock-in resize after 2000ms to ensure aspect ratio matches stabilized stream
        this.finalResizeTimeout = window.setTimeout(() => {
            this.applyVideoResize();
        }, 2000);
        this.initVideo();
    }

    playVideo(stream: MediaStream, senderId: number) {
        if (this.attemptingPlay) {
            Logger.debug(MODULE, "Already attempting to play video, ignoring duplicate call");
        } else {
            this.releaseUrlAudioOwnership();
            this.beginNewSource();

            if (!this.video) {
                Logger.warn(MODULE, 'Video element not initialized');
                this.attemptingPlay = false;
                return;
            }

            this.video.srcObject = stream;
            this.currentSenderId = senderId;
            this.currentSourceKind = 'stream';
            this.attachCurrentStreamListeners(stream, senderId);

            if(this.playerBased && this.mesh) {
                this.mesh.active = true;
            }
                            
            const phongMaterial = this.material as PhongOpaque;
            phongMaterial.setDiffuseColor([.75, .75, .75, 1]); // Sets a White diffuse color

            this.video.play().catch((error) => {
                if ((error as Error)?.name === 'AbortError') {
                    Logger.debug(MODULE, 'Video play aborted during stop/reset');
                    return;
                }
                Logger.warn(MODULE, 'Video play failed', error);
            });
            this.frameUpdateRequested = true;
        }
    }

    private releaseUrlAudioOwnership() {
        if (NetworkListenVideoTexture.urlAudioOwner === this) {
            NetworkListenVideoTexture.urlAudioOwner = null;
        }
        this.ownsUrlAudio = false;
    }

    private attachCurrentStreamListeners(stream: MediaStream, senderId: number) {
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack) {
            return;
        }

        this.currentTrackEndedHandler = () => {
            Logger.debug(MODULE, `Video track ended for sender ${senderId}, clearing texture`);
            this.onShareStopped(senderId);
        };
        videoTrack.addEventListener('ended', this.currentTrackEndedHandler);

        this.currentRemoveTrackHandler = (event: MediaStreamTrackEvent) => {
            if (event.track.kind === 'video') {
                Logger.debug(MODULE, `Video track removed from stream for sender ${senderId}, clearing texture`);
                this.onShareStopped(senderId);
            }
        };
        stream.addEventListener('removetrack', this.currentRemoveTrackHandler);
    }

    private detachCurrentStreamListeners() {
        const stream = this.video?.srcObject as MediaStream | null;
        const videoTrack = stream?.getVideoTracks?.()[0];

        if (videoTrack && this.currentTrackEndedHandler) {
            videoTrack.removeEventListener('ended', this.currentTrackEndedHandler);
        }
        if (stream && this.currentRemoveTrackHandler) {
            stream.removeEventListener('removetrack', this.currentRemoveTrackHandler);
        }

        this.currentTrackEndedHandler = null;
        this.currentRemoveTrackHandler = null;
    }

    private clearVideoSurface() {
        this.detachCurrentStreamListeners();
        this.urlSync.clear();
        this.releaseUrlAudioOwnership();
        this.currentSenderId = null;
        this.currentSourceKind = null;
        this.video?.pause();
        if (this.video) {
            this.video.srcObject = null;
            this.video.removeAttribute('src');
        }
        this.loaded = false;
        this.attemptingPlay = false;
        this.texture?.destroy();
        delete this.texture;
        if (this.playerBased && this.mesh) {
            this.mesh.active = false;
        }
    }

    onDestroy() {
        this.detachCurrentStreamListeners();
        if (this.videoSizeChangeTimeout !== undefined) {
            clearTimeout(this.videoSizeChangeTimeout);
        }
        if (this.initialResizeTimeout !== undefined) {
            clearTimeout(this.initialResizeTimeout);
        }
        if (this.finalResizeTimeout !== undefined) {
            clearTimeout(this.finalResizeTimeout);
        }
        if (this.masterSharerClaimHandler && !this.playerBased) {
            this.vidshareEventsClient.onClaimMasterShareEvent.remove(this.masterSharerClaimHandler);
            this.vidshareEventsClient.onSyncUrlVideoStartEvent.remove(this.onSyncUrlVideoStartHandler);
            this.vidshareEventsClient.onSyncUrlVideoPingEvent.remove(this.onSyncUrlVideoPingHandler);
        }
        this.urlSync.clear();
        this.releaseUrlAudioOwnership();
        this.video?.remove();
        this.texture?.destroy();
        if(this.playerBased && this.mesh) {
            this.mesh.active = false;
        }
    }

    applyTexture() {
        // Safety check: ensure video has valid dimensions before creating texture
        if (!this.video || this.video.videoWidth === 0 || this.video.videoHeight === 0) {
            Logger.debug(MODULE, `Cannot apply texture: video not ready (dimensions: ${this.video?.videoWidth || 0}x${this.video?.videoHeight || 0})`);
            return;
        }

        const video = this.video as HTMLVideoElement;
        const mat = this.material;
        const pipeline = mat.pipeline;
        
        const texture = (this.texture = this.engine.textures.create(video));

        if (!setFirstMaterialTexture(mat, texture, this.textureProperty)) {
            Logger.error(MODULE, 'Pipeline ' + pipeline + ' not supported by video-texture');
        }

        // Skip initial resize - it'll be handled by resize events after stabilization

        if (typeof (video as any).requestVideoFrameCallback === 'function') {
            (video as any).requestVideoFrameCallback(this.updateVideo.bind(this));
        } else {
            video.addEventListener('timeupdate', () => {
                this.frameUpdateRequested = true;
            });
        }

        // Clear the initial texture creation flag so future resize events can apply resizing
        this.isInitialTextureCreation = false;
    }

    update(dt: number) {
        if (this.loaded && this.attemptingPlay) {
            this.attemptingPlay = false;
        }
        if (this.loaded && this.frameUpdateRequested && this.video && this.video.readyState >= this.video.HAVE_CURRENT_DATA) {
            if (this.texture) {
                this.texture.update();
            } else {
                /* Apply texture on first frame update request */
                this.applyTexture();
            }

            // Finalize aspect ratio once the first real frame is available
            if (!this.firstResizeApplied && this.video.videoWidth > 0 && this.video.videoHeight > 0) {
                resizeToRatio(this.video.videoWidth, this.video.videoHeight, this.canvasMaxScaleWidth, this.canvasMaxScaleHeight, this.object);
                this.lastAppliedWidth = this.video.videoWidth;
                this.lastAppliedHeight = this.video.videoHeight;
                this.firstResizeApplied = true;
            }

            this.frameUpdateRequested = false;
        }
    }

    updateVideo() {
        this.frameUpdateRequested = true;
        if (this.video && 'requestVideoFrameCallback' in this.video) {
            this.video.requestVideoFrameCallback(this.updateVideo.bind(this));
        }
    }
}